import React from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Select,
  CircularProgress,
  FormControlLabel,
  Radio,
  FormLabel,
} from '@mui/material';
import {
  FormPageContainer,
  FormHeader,
  FormSubHeader,
  StyledForm,
  SubmitButton,
  SectionSubHeader,
  RadioGroupContainer,
  StyledRadioGroup,
  ButtonGroupContainer, 
  BackButton
} from './MaritalStatusForm.styles'; // Import specific styles

export default function MaritalStatusForm({ formData, handleChange, handleSubmit, handleBack, isSubmitting, setFormData }) {
  
  // Helper function to handle radio button changes for boolean values
  const handleRadioChange = (event) => {
    const { name, value } = event.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value === 'true' // Convert string 'true'/'false' back to boolean
    }));
  };
  
  return (
    <FormPageContainer>
      <FormHeader variant="h4" component="h1">Will & Testament Creator</FormHeader>
      <FormSubHeader variant="h5" component="h2">Step 2: Marital Status</FormSubHeader>
      <StyledForm component="form" onSubmit={handleSubmit} noValidate>
        
        <FormControl fullWidth required sx={{ padding: '12px' }}> 
          <InputLabel id="maritalStatus-label" sx={{ padding: '12px' }}>Marital Status</InputLabel>
          <Select
            labelId="maritalStatus-label"
            id="maritalStatus"
            name="maritalStatus"
            value={formData.maritalStatus}
            label="Marital Status"
            onChange={handleChange}
          >
            <MenuItem value=""><em>Select</em></MenuItem>
            <MenuItem value="single">Single (Never Married)</MenuItem>
            <MenuItem value="married">Married</MenuItem>
            <MenuItem value="divorced">Divorced</MenuItem>
            <MenuItem value="widowed">Widowed</MenuItem>
            <MenuItem value="customary">Customary/Religious Marriage</MenuItem>
          </Select>
        </FormControl>

        {formData.maritalStatus === "married" && (
          <>
            <FormControl fullWidth required sx={{ padding: '12px' }}>
              <InputLabel id="marriageType-label" sx={{ padding: '12px' }}>How are you married?</InputLabel>
              <Select
                labelId="marriageType-label"
                id="marriageType"
                name="marriageType"
                value={formData.marriageType}
                label="How are you married?"
                onChange={handleChange}
              >
                <MenuItem value=""><em>Select</em></MenuItem>
                <MenuItem value="In Community of Property">
                  In Community of Property
                </MenuItem>
                <MenuItem value="Out of Community of Property (With Accrual)">
                  Out of Community of Property (With Accrual)
                </MenuItem>
                <MenuItem value="Out of Community of Property (Without Accrual)">
                  Out of Community of Property (Without Accrual)
                </MenuItem>
              </Select>
            </FormControl>

            <SectionSubHeader variant="h6">Spouse Details</SectionSubHeader>
            
            <FormControl fullWidth required sx={{ padding: '12px' }}>
              <InputLabel id="spouseTitle-label" sx={{ padding: '12px' }}>Title</InputLabel>
              <Select
                labelId="spouseTitle-label"
                id="spouseTitle"
                name="spouseTitle"
                value={formData.spouseTitle}
                label="Title"
                onChange={handleChange}
              >
                 <MenuItem value=""><em>Select</em></MenuItem>
                 <MenuItem value="Mr">Mr</MenuItem>
                 <MenuItem value="Mrs">Mrs</MenuItem>
                 <MenuItem value="Ms">Ms</MenuItem>
                 <MenuItem value="Dr">Dr</MenuItem>
                 <MenuItem value="Prof">Prof</MenuItem>
              </Select>
            </FormControl>

            <TextField
              id="spouseFirstName"
              name="spouseFirstName"
              label="First Name(s)"
              value={formData.spouseFirstName}
              onChange={handleChange}
              required
              fullWidth
              sx={{ padding: '12px' }}
            />

            <TextField
              id="spouseLastName"
              name="spouseLastName"
              label="Last Name"
              value={formData.spouseLastName}
              onChange={handleChange}
              required
              fullWidth
              sx={{ padding: '12px' }}
            />

            <TextField
              id="spouseIdNumber"
              name="spouseIdNumber"
              label="ID Number"
              value={formData.spouseIdNumber}
              onChange={handleChange}
              required
              fullWidth
              sx={{ padding: '12px' }}
            />
          </>
        )}

        {formData.maritalStatus === "divorced" && (
          <RadioGroupContainer component="fieldset" variant="standard">
            <FormLabel component="legend">Would you like to include your ex-spouse as a beneficiary?</FormLabel>
            <StyledRadioGroup
              aria-label="include-ex-spouse"
              name="includeExSpouse"
              value={formData.includeExSpouse.toString()} // RadioGroup expects string value
              onChange={handleRadioChange} 
            >
              <FormControlLabel value="true" control={<Radio />} label="Yes" />
              <FormControlLabel value="false" control={<Radio />} label="No" />
            </StyledRadioGroup>
          </RadioGroupContainer>
        )}

        {formData.maritalStatus === "widowed" && (
           <RadioGroupContainer component="fieldset" variant="standard">
            <FormLabel component="legend">Would you like to allocate any inheritance in their memory?</FormLabel>
             <StyledRadioGroup
               aria-label="allocate-inheritance"
               name="allocateInheritance"
               value={formData.allocateInheritance.toString()}
               onChange={handleRadioChange}
             >
               <FormControlLabel value="true" control={<Radio />} label="Yes" />
               <FormControlLabel value="false" control={<Radio />} label="No" />
             </StyledRadioGroup>
           </RadioGroupContainer>
        )}

        {formData.maritalStatus === "customary" && (
          <>
            <RadioGroupContainer component="fieldset" variant="standard">
              <FormLabel component="legend">Do you have multiple spouses?</FormLabel>
               <StyledRadioGroup
                 aria-label="multiple-spouses"
                 name="multipleSpouses"
                 value={formData.multipleSpouses.toString()}
                 onChange={handleRadioChange}
               >
                 <FormControlLabel value="true" control={<Radio />} label="Yes" />
                 <FormControlLabel value="false" control={<Radio />} label="No" />
               </StyledRadioGroup>
             </RadioGroupContainer>

             <RadioGroupContainer component="fieldset" variant="standard">
              <FormLabel component="legend">Is it a legal Marriage?</FormLabel>
               <StyledRadioGroup
                 aria-label="legal-marriage"
                 name="legalMarriage"
                 value={formData.legalMarriage.toString()}
                 onChange={handleRadioChange}
               >
                 <FormControlLabel value="true" control={<Radio />} label="Yes" />
                 <FormControlLabel value="false" control={<Radio />} label="No" />
               </StyledRadioGroup>
             </RadioGroupContainer>
          </>
        )}

        <ButtonGroupContainer>
          <BackButton 
              type="button" 
              variant="contained" 
              onClick={handleBack}
              disabled={isSubmitting}
          >
            Back
          </BackButton>
          <SubmitButton
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
            onClick={(e) => { handleSubmit(e) }}
          >
            {isSubmitting ? 'Processing...' : 'Next'}
          </SubmitButton>
        </ButtonGroupContainer>
      </StyledForm>
    </FormPageContainer>
  );
} 